import React, { useCallback, useEffect, useState } from "react";
import { getNHICSettings } from "./nhicApi";
import NHICSettingsModal from "./components/NHICSettingsModal";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AppTable from "../../shared/AppTable";
import AppButton from "../../shared/AppButton";
import AddIcon from "@mui/icons-material/Add";
import { Chip } from "@mui/material";
import { CheckCircle, Cancel, Key } from "@mui/icons-material";
import TableSkeletonLoader from "../../common/TableLoading";

function NHICSettings() {
  const { t } = useTranslation();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getNHICSettings();
      setData({ getNHICSettings: response });
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch NHIC settings",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleEdit = () => {
    if (data?.getNHICSettings?.data) {
      setEditData(data.getNHICSettings.data);
      setShowSettingsModal(true);
    }
  };

  const handleAddNew = () => {
    setEditData(null);
    setShowSettingsModal(true);
  };

  const settingsData = data?.getNHICSettings?.data;
  const hasSettings = settingsData && settingsData.configs?.length > 0;

  // Prepare table data
  const tableData = hasSettings
    ? settingsData.configs.map((config) => ({
        ...config,
        auth_base_url: settingsData.auth_base_url,
      }))
    : [];

  const columns = [
    {
      header: t("Type"),
      render: (row) => (
        <Chip
          label={row.type}
          size="small"
          sx={{
            backgroundColor: row.type === "patient" ? "#dbeafe" : "#d1fae5",
            color: row.type === "patient" ? "#1e40af" : "#065f46",
            fontWeight: "700",
            fontSize: "11px",
            textTransform: "uppercase",
            borderRadius: "6px",
          }}
        />
      ),
    },
    {
      header: t("Base URL"),
      render: (row) => (
        <span className="text-xs font-medium text-gray-700 block max-w-[300px] truncate">
          {row.base_url}
        </span>
      ),
    },
    {
      header: t("Consumer Key"),
      render: (row) => (
        <div className="flex items-center gap-2">
          <Key sx={{ fontSize: 16, color: "#6b7280" }} />
          <span className="text-xs font-mono text-gray-600 max-w-[200px] truncate">
            {row.consumer_key}
          </span>
        </div>
      ),
    },
    {
      header: t("Consumer Secret"),
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.consumer_secret_exists ? (
            <>
              <CheckCircle sx={{ fontSize: 16, color: "#10b981" }} />
              <span className="text-xs text-green-600 font-semibold">
                {t("Configured")}
              </span>
            </>
          ) : (
            <>
              <Cancel sx={{ fontSize: 16, color: "#ef4444" }} />
              <span className="text-xs text-red-600 font-semibold">
                {t("Not Set")}
              </span>
            </>
          )}
        </div>
      ),
    },
    {
      header: t("Access Token"),
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.access_token_exists ? (
            <>
              <CheckCircle sx={{ fontSize: 16, color: "#10b981" }} />
              <span className="text-xs text-green-600 font-semibold">
                {t("Active")}
              </span>
            </>
          ) : (
            <>
              <Cancel sx={{ fontSize: 16, color: "#f59e0b" }} />
              <span className="text-xs text-yellow-600 font-semibold">
                {t("Inactive")}
              </span>
            </>
          )}
        </div>
      ),
    },
    // {
    //   header: t("Provider ID"),
    //   render: (row) => (
    //     <span className="text-xs text-gray-600">
    //       {row.providers_id || "---"}
    //     </span>
    //   ),
    // },
    // {
    //   header: t("Branch ID"),
    //   render: (row) => (
    //     <span className="text-xs text-gray-600">
    //       {row.providers_branch_id || "---"}
    //     </span>
    //   ),
    // },
    {
      header: t("Created At"),
      render: (row) => (
        <span className="text-xs text-gray-600">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      ),
    },
  ];

  return (
    <div className="w-full !p-0">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <div className="w-full">
          <span className="font-semibold text-black/70 text-lg">
            {t("NHIC Settings")}
            <span className="text-lg ml-1">
              ({tableData.length}{" "}
              <span className="text-xs font-bold italic">{t("Entries")}</span>)
            </span>
          </span>
        </div>
        <div className="flex gap-2">
          <AppButton
            label={hasSettings ? t("Update Settings") : t("Configure Settings")}
            onClick={hasSettings ? handleEdit : handleAddNew}
            startIcon={<AddIcon />}
          />
        </div>
      </div>

      {hasSettings && settingsData.auth_base_url && (
        <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs">
          <span className="font-bold text-slate-600">
            {t("Authentication Base URL")}:
          </span>
          <span className="font-medium text-slate-700 break-all">
            {settingsData.auth_base_url}
          </span>
        </div>
      )}

      {/* Table */}
      <AppTable
        columns={columns}
        data={tableData}
        loading={loading}
        actions={false}
        LoaderComponent={<TableSkeletonLoader columnCount={columns.length} />}
        containerClassName="custom-table-scrollbar"
      />

      {/* Settings Modal */}
      {showSettingsModal && (
        <NHICSettingsModal
          isOpen={showSettingsModal}
          onClose={() => {
            setShowSettingsModal(false);
            setEditData(null);
          }}
          editData={editData}
          refetch={refetch}
        />
      )}
    </div>
  );
}

export default NHICSettings;
